import {Injectable} from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {Observable, map} from 'rxjs';
import {buildPath} from '@utils/util-builder';
import {ApiResponse} from '@models/api.response';
import {PolizaResponse, PolizaRequest, PolizaStatusRequest, PolizaPagoRequest, PolizaContratantesRequest} from '@models/poliza.interface';
import {GenericSelect} from '@models/generic-select.interface';
import {PolizaStatus} from '@enums/PolizaStatus';

@Injectable({
    providedIn: 'root'
})
export class PolizaService {
    private readonly _basePath: string = 'data-core/poliza';

    constructor(private readonly _http: HttpClient) {
    }

    getPolizasByProveedor(): Observable<PolizaResponse[]> {
        return this._http.get<ApiResponse<PolizaResponse[]>>(buildPath([this._basePath, 'proveedor']))
            .pipe(map((response: ApiResponse<PolizaResponse[]>): PolizaResponse[] => response.data));
    }

    getAllPolizas(filtros?: { proveedorId?: number; estatus?: PolizaStatus; contratanteId?: number }): Observable<PolizaResponse[]> {
        let params: HttpParams = new HttpParams();
        if (filtros?.proveedorId != null) {
            params = params.set('proveedorId', filtros.proveedorId.toString());
        }
        if (filtros?.estatus != null) {
            params = params.set('estatus', filtros.estatus);
        }
        if (filtros?.contratanteId != null) {
            params = params.set('contratanteId', filtros.contratanteId.toString());
        }
        return this._http.get<ApiResponse<PolizaResponse[]>>(buildPath([this._basePath, 'all']), {params})
            .pipe(map((response: ApiResponse<PolizaResponse[]>): PolizaResponse[] => response.data));
    }

    getPolizasByCompany(filtros?: { proveedorId?: number; estatus?: PolizaStatus }): Observable<PolizaResponse[]> {
        let params: HttpParams = new HttpParams();
        if (filtros?.proveedorId != null) {
            params = params.set('proveedorId', filtros.proveedorId.toString());
        }
        if (filtros?.estatus != null) {
            params = params.set('estatus', filtros.estatus);
        }
        return this._http.get<ApiResponse<PolizaResponse[]>>(buildPath([this._basePath, 'company']), {params})
            .pipe(map((response: ApiResponse<PolizaResponse[]>): PolizaResponse[] => response.data));
    }

    createPoliza(polizaData: PolizaRequest, archivoPdf: File): Observable<PolizaResponse> {
        const formData: FormData = new FormData();
        formData.append('polizaData', new Blob([JSON.stringify(polizaData)], {type: 'application/json'}));
        formData.append('archivoPdf', archivoPdf);
        return this._http.post<ApiResponse<PolizaResponse>>(buildPath([this._basePath]), formData)
            .pipe(map((response: ApiResponse<PolizaResponse>): PolizaResponse => response.data));
    }

    getContratantesDisponibles(proveedorId?: number): Observable<GenericSelect[]> {
        let params: HttpParams = new HttpParams();
        if (proveedorId != null) {
            params = params.set('proveedorId', proveedorId.toString());
        }
        return this._http.get<ApiResponse<GenericSelect[]>>(buildPath([this._basePath, 'contratantes-disponibles']), {params})
            .pipe(map((response: ApiResponse<GenericSelect[]>): GenericSelect[] => response.data));
    }

    descargarPdf(id: number): Observable<Blob> {
        return this._http.get(buildPath([this._basePath, id.toString(), 'pdf']), {responseType: 'blob'});
    }

    deletePoliza(id: number): Observable<void> {
        return this._http.delete<void>(buildPath([this._basePath, id.toString()]));
    }

    updateEstatus(id: number, payload: PolizaStatusRequest): Observable<PolizaResponse> {
        return this._http.patch<ApiResponse<PolizaResponse>>(buildPath([this._basePath, id.toString(), 'estatus']), payload)
            .pipe(map((response: ApiResponse<PolizaResponse>): PolizaResponse => response.data));
    }

    updatePago(id: number, payload: PolizaPagoRequest): Observable<PolizaResponse> {
        return this._http.patch<ApiResponse<PolizaResponse>>(buildPath([this._basePath, id.toString(), 'pago']), payload)
            .pipe(map((response: ApiResponse<PolizaResponse>): PolizaResponse => response.data));
    }

    updateContratantes(id: number, payload: PolizaContratantesRequest): Observable<PolizaResponse> {
        return this._http.put<ApiResponse<PolizaResponse>>(buildPath([this._basePath, id.toString(), 'contratantes']), payload)
            .pipe(map((response: ApiResponse<PolizaResponse>): PolizaResponse => response.data));
    }

    /** Edición completa de una póliza por MASTER (PUT /poliza/{id}, multipart, sin restricción de estatus). */
    updateAsMaster(id: number, polizaData: PolizaRequest, archivoPdf?: File): Observable<PolizaResponse> {
        const formData: FormData = new FormData();
        formData.append('polizaData', new Blob([JSON.stringify(polizaData)], {type: 'application/json'}));
        if (archivoPdf) {
            formData.append('archivoPdf', archivoPdf);
        }
        return this._http.put<ApiResponse<PolizaResponse>>(buildPath([this._basePath, id.toString()]), formData)
            .pipe(map((response: ApiResponse<PolizaResponse>): PolizaResponse => response.data));
    }
}
